/**
 * The flyable helicopter: its airframe, and the flight model that moves it.
 *
 * The model is arcade, but it is a real *helicopter* model rather than a
 * free-flying camera: thrust always points along the rotor mast, so the only
 * way to go anywhere is to tilt the disc and let the horizontal component of
 * lift pull you there. Nose down to accelerate, flare back to stop, bank to
 * turn. That single constraint is what makes it feel like a helicopter.
 *
 * `stepHelicopter` is deliberately free of Three types so the flight model can
 * be tested — and ported — on its own.
 */

import {
  AdditiveBlending,
  BackSide,
  BoxGeometry,
  CanvasTexture,
  CapsuleGeometry,
  CatmullRomCurve3,
  CircleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  ExtrudeGeometry,
  Group,
  LatheGeometry,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Shape,
  SphereGeometry,
  SpotLight,
  Sprite,
  SpriteMaterial,
  TubeGeometry,
  Vector2,
  Vector3,
} from 'three';
import type { BufferGeometry, Material, Object3D, Scene } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

export const HELI = {
  /** Maximum disc tilt in radians. Sets the top speed with `drag`. */
  maxTilt: 0.7,
  /** How fast the disc tilts toward the stick, radians per second. */
  tiltRate: 2.4,
  /** Pedal authority, radians per second. */
  yawRate: 1.5,
  /**
   * Ceiling on how fast a bank alone pulls the nose round, radians per second.
   *
   * Folding turn coordination in means banking alone turns you, which is what
   * everyone expects from a stick. The *rate* is derived from the bank angle
   * and speed (see `stepHelicopter`); this only caps it at low speed, where the
   * true expression diverges.
   */
  turnCoordination: 1.0,
  /**
   * Speed floor used when deriving the coordinated turn rate, m/s.
   * Below this the machine is effectively hovering and pivots on the pedals.
   */
  minTurnSpeed: 8,
  /** Extra acceleration available on the collective, m/s^2. */
  climbAuthority: 13,
  /** Quadratic drag on horizontal motion. Lower is faster. */
  drag: 0.0011,
  /** Linear damping on climb and descent, so vertical speed settles. */
  verticalDrag: 0.55,
  /** Hard clamp on speed, m/s. */
  maxSpeed: 120,
  gravity: 9.81,
  /** Main rotor angular speed, radians per second. */
  rotorSpeed: 32,
  /** Height of the skids, so the airframe rests above the ground. */
  skidHeight: 1.15,
} as const;

/**
 * The lowest `cos(tilt)` used when working out how hard to pull on the
 * collective. Without a floor, a disc tilted toward vertical would demand
 * infinite thrust.
 */
const MIN_COS_TILT = 0.35;

export interface HeliVelocity {
  /** Scene axes: x east, y up, z south. Metres per second. */
  x: number;
  y: number;
  z: number;
}

export interface HeliState {
  velocity: HeliVelocity;
  /** Negative is nose-down. */
  pitch: number;
  /** Negative is banked right, matching Three's Z rotation. */
  roll: number;
}

export interface HeliInput {
  /** +1 pushes the nose down (accelerate forward). */
  cyclicPitch: number;
  /** +1 banks right. */
  cyclicRoll: number;
  /** +1 yaws right. */
  yaw: number;
  /** +1 climbs, -1 descends, 0 holds altitude. */
  collective: number;
}

export const HOVER: HeliState = {
  velocity: { x: 0, y: 0, z: 0 },
  pitch: 0,
  roll: 0,
};

/**
 * The direction the rotor mast points, as a unit vector in scene axes.
 *
 * This is the body "up" axis carried through the same YXZ rotation the airframe
 * is drawn with, so the thrust the physics applies and the attitude you see are
 * guaranteed to agree.
 */
export function rotorAxis(yaw: number, pitch: number, roll: number): HeliVelocity {
  const cr = Math.cos(roll);
  // Body up, after roll about Z and pitch about X.
  const bx = -Math.sin(roll);
  const by = cr * Math.cos(pitch);
  const bz = cr * Math.sin(pitch);
  // Then yaw about Y.
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  return { x: cy * bx + sy * bz, y: by, z: -sy * bx + cy * bz };
}

export interface HeliStep {
  state: HeliState;
  /** Change in scene yaw this tick. */
  yawDelta: number;
}

export function stepHelicopter(
  state: HeliState,
  input: HeliInput,
  yaw: number,
  dt: number,
): HeliStep {
  // Nose down is negative pitch, so forward stick is negated.
  const pitch = approach(state.pitch, -input.cyclicPitch * HELI.maxTilt, HELI.tiltRate * dt);
  const roll = approach(state.roll, -input.cyclicRoll * HELI.maxTilt, HELI.tiltRate * dt);

  // Pedals, plus the turn the bank is already producing.
  //
  // The rate has to be the one the bank can actually fly — g*tan(bank)/v — not
  // a constant. A constant swings the nose far faster than the flight path can
  // curve at speed (1.0 rad/s of nose against 0.13 rad/s of path at 60 m/s),
  // so the machine ends up flying sideways with its thrust pointing partly
  // backwards, and a banked turn scrubs off most of its speed.
  //
  // The expression diverges as v approaches zero, hence both the speed floor
  // and the cap: near a hover, banking pivots you at a fixed comfortable rate.
  const horizontalSpeed = Math.hypot(state.velocity.x, state.velocity.z);
  const coordinated = Math.min(
    HELI.turnCoordination,
    (HELI.gravity * Math.abs(Math.tan(roll))) / Math.max(HELI.minTurnSpeed, horizontalSpeed),
  );
  // Roll is negative when banked right, and scene yaw decreases as heading
  // increases, so a right bank must decrease yaw.
  const yawDelta = (-input.yaw * HELI.yawRate + Math.sign(roll) * coordinated) * dt;

  const axis = rotorAxis(yaw, pitch, roll);

  // Collective is trimmed for level flight: neutral stick holds altitude at any
  // attitude, because the thrust needed grows as 1/cos(tilt). Without this the
  // player would be fighting the collective every time they tilted, which is
  // realistic and miserable.
  const thrust = HELI.gravity / Math.max(MIN_COS_TILT, axis.y) + input.collective * HELI.climbAuthority;

  let vx = state.velocity.x + axis.x * thrust * dt;
  let vy = state.velocity.y + (axis.y * thrust - HELI.gravity) * dt;
  let vz = state.velocity.z + axis.z * thrust * dt;

  // Quadratic drag on the horizontal, which is what sets the top speed.
  const horizontal = Math.hypot(vx, vz);
  if (horizontal > 0) {
    const decel = Math.min(horizontal, HELI.drag * horizontal * horizontal * dt);
    vx -= (vx / horizontal) * decel;
    vz -= (vz / horizontal) * decel;
  }
  // Linear damping vertically, so climb and descent settle at a rate rather
  // than accelerating without limit.
  vy -= vy * Math.min(1, HELI.verticalDrag * dt);

  const speed = Math.hypot(vx, vy, vz);
  if (speed > HELI.maxSpeed) {
    const k = HELI.maxSpeed / speed;
    vx *= k;
    vy *= k;
    vz *= k;
  }

  return { state: { velocity: { x: vx, y: vy, z: vz }, pitch, roll }, yawDelta };
}

/**
 * Settle the airframe onto the ground.
 *
 * Called when the skids are at or below the terrain: cancels any downward
 * velocity and scrubs off ground speed, so a landed helicopter stays put
 * instead of sliding across the hillside.
 */
export function settleOnGround(state: HeliState, dt: number): HeliState {
  const friction = Math.max(0, 1 - 4 * dt);
  return {
    pitch: state.pitch,
    roll: state.roll,
    velocity: {
      x: state.velocity.x * friction,
      y: Math.max(0, state.velocity.y),
      z: state.velocity.z * friction,
    },
  };
}

function approach(current: number, target: number, maxStep: number): number {
  const delta = target - current;
  if (Math.abs(delta) <= maxStep) return target;
  return current + Math.sign(delta) * maxStep;
}

// --- the airframe ------------------------------------------------------------
//
// Built once and shared: every helicopter in the world draws the same merged
// geometry, one mesh per material, so a detailed airframe is still about a
// dozen draw calls. Only the lights are each machine's own, as they flash on
// the one being flown and stay dark on the ones parked.

const faceted = (color: number, roughness: number, metalness = 0, extra: Partial<MeshStandardMaterial> = {}) => {
  const m = new MeshStandardMaterial({ color: new Color(color), roughness, metalness });
  Object.assign(m, extra);
  return m;
};
// An air ambulance's livery: white, a navy flash along the flanks, red bands on the boom.
const paint = faceted(0xeeebe4, 0.3, 0.15);
const stripe = faceted(0x1d3a57, 0.35, 0.3);
const accent = faceted(0xd4412f, 0.45, 0.1);
/** Tinted, so whoever is flying can be seen inside. */
const glass = faceted(0x2d4660, 0.06, 0.5, { transparent: true, opacity: 0.36, depthWrite: false });
const dark = faceted(0x1b1c20, 0.75, 0.2);
const metal = faceted(0x9aa1a8, 0.35, 0.8);
/** The cabin's inside, seen through the glass and the open door. */
const lining = faceted(0x4a4d53, 0.9, 0, { side: BackSide });
const fabric = faceted(0x30353f, 0.95);

/** Height of the fuselage's centre line over the ground, and of the tail boom's. */
const BODY_Y = HELI.skidHeight + 0.95;
/** A touch over the centre line, so the boom leaves the top of the tail cone as it tapers. */
const BOOM_Y = HELI.skidHeight + 1.03;
const MAST_Z = -0.3;
const ROTOR_Y = HELI.skidHeight + 2.45;

/**
 * The fuselage's radius along its length: z (nose negative) and radius.
 * Turned on a lathe, so the nose is a proper bubble rather than a capsule.
 */
const PROFILE: Array<[number, number]> = [
  [-2.95, 0.02], [-2.85, 0.32], [-2.65, 0.58], [-2.35, 0.8], [-1.95, 0.95], [-1.4, 1.02], [-0.6, 1.04],
  [0.2, 1.0], [0.8, 0.88], [1.25, 0.66], [1.6, 0.42], [1.85, 0.3],
  // Closed to a point inside the boom's root: an open end shows the dark inside.
  [2.0, 0.2], [2.12, 0.0],
];

/**
 * A stretch of the fuselage between `z0` and `z1`, all round or only from
 * `phiStart` for `phiLength` — phi 0 is the belly, π the roof, π/2 the right
 * side — and stood `lift` metres off it, so glass and stripes lie over the
 * paint without flickering into it, right to the tip of the nose.
 */
function hull(z0: number, z1: number, lift = 0, phiStart = 0, phiLength = Math.PI * 2): BufferGeometry {
  // Sampled on its own ends, so pieces cut at the same z meet exactly.
  const a = Math.max(z0, PROFILE[0][0]), b = Math.min(z1, PROFILE[PROFILE.length - 1][0]);
  const steps = Math.max(2, Math.ceil((b - a) / 0.12));
  const pts: Vector2[] = [];
  for (let i = 0; i <= steps; i++) {
    const z = a + ((b - a) * i) / steps;
    pts.push(new Vector2(radiusAt(z) + lift, z));
  }
  // Segments in proportion to the arc, so a strip and the whole agree.
  const segments = Math.max(3, Math.round((32 * phiLength) / (Math.PI * 2)));
  const g = new LatheGeometry(pts, segments, phiStart, phiLength);
  // Lathe axis Y becomes the length, Z; a touch taller than wide.
  g.rotateX(Math.PI / 2);
  g.scale(1, 1.1, 1);
  g.translate(0, BODY_Y, 0);
  return g;
}

/** The fuselage's radius at z: smoothstep between profile points, a curve rather than facets. */
function radiusAt(z: number): number {
  for (let i = 0; i < PROFILE.length - 1; i++) {
    const [za, ra] = PROFILE[i], [zb, rb] = PROFILE[i + 1];
    if (z > zb) continue;
    const t = Math.max(0, (z - za) / (zb - za)), s = t * t * (3 - 2 * t);
    return ra + (rb - ra) * s;
  }
  return PROFILE[PROFILE.length - 1][1];
}

/** A side profile (z, y pairs), extruded `thickness` across and centred on x = `x`. */
function fin(points: Array<[number, number]>, thickness: number, x = 0): BufferGeometry {
  const shape = new Shape();
  shape.moveTo(points[0][0], points[0][1]);
  for (const [z, y] of points.slice(1)) shape.lineTo(z, y);
  const g = new ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.02, bevelSegments: 1 });
  g.rotateY(-Math.PI / 2);
  g.translate(x + thickness / 2, 0, 0);
  return g;
}

function placed(g: BufferGeometry, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): BufferGeometry {
  g.rotateX(rx); g.rotateY(ry); g.rotateZ(rz);
  g.translate(x, y, z);
  return g;
}

/** A tube along `points`, for the skids' cross tubes. */
function tube(points: Array<[number, number, number]>, radius: number): BufferGeometry {
  return new TubeGeometry(new CatmullRomCurve3(points.map(([x, y, z]) => new Vector3(x, y, z))), 16, radius, 6);
}

function merge(parts: BufferGeometry[]): BufferGeometry {
  // Lathes and tubes are indexed, extrusions not: all flattened alike.
  const merged = mergeGeometries(parts.map(p => (p.index === null ? p : p.toNonIndexed())));
  for (const p of parts) p.dispose();
  merged.computeBoundingSphere();
  return merged;
}

interface Airframe {
  body: Array<[BufferGeometry, Material]>;
  /** The pilot's door, about its hinge at `doorHinge`. */
  door: Array<[BufferGeometry, Material]>;
  rotor: Array<[BufferGeometry, Material]>;
  tail: BufferGeometry;
  disc: BufferGeometry;
  light: BufferGeometry;
  lens: BufferGeometry;
}

let airframe: Airframe | undefined;

// Where the glass is, round the fuselage: phi 0 is the belly, π/2 the right
// side, π the roof, 3π/2 the left. The pilot's door is on the left.
const TAU = Math.PI * 2;
const NOSE_Z = -1.35;
const CABIN_Z = 0.15;
const NOSE_GLASS: [number, number] = [1.02, 5.26];
const RIGHT_WINDOW: [number, number] = [1.32, 2.27];
const DOOR_WINDOW: [number, number] = [4.01, 4.96];
/** The door's lower edge, round from its window. */
const DOOR_SILL = 5.42;
const STRIPE_RIGHT: [number, number] = [0.87, 1.19];
const STRIPE_LEFT: [number, number] = [TAU - 1.19, TAU - 0.87];
const span = ([a, b]: [number, number]): [number, number] => [a, b - a];

/** Where the pilot sits and reaches, in the airframe's frame: the seat on the left. */
export const HELI_SEAT = {
  x: -0.4, hipY: 1.72, hipZ: -0.72,
  /** The cyclic's grip, and the pedals. */
  wheelY: 1.92, wheelZ: -1.12, pedalY: 1.36, pedalZ: -1.6,
  /** Where to stand outside the door. */
  outsideX: -1.85,
};
/** The boarding step on the left skid, and the door's hinge on its front edge. */
export const HELI_STEP = { x: -1.22, y: 0.58, z: -0.8 };
const DOOR_HINGE = new Vector3(-radiusAt(NOSE_Z), BODY_Y, NOSE_Z);

function buildAirframe(): Airframe {
  const shell = merge([
    // The painted skin, with the glass and the door cut out of it.
    hull(-3, NOSE_Z, 0, NOSE_GLASS[1], TAU - (NOSE_GLASS[1] - NOSE_GLASS[0])),
    hull(NOSE_Z, CABIN_Z, 0, RIGHT_WINDOW[1], DOOR_WINDOW[0] - RIGHT_WINDOW[1]),
    hull(NOSE_Z, CABIN_Z, 0, DOOR_SILL, TAU + RIGHT_WINDOW[0] - DOOR_SILL),
    hull(CABIN_Z, 3),
    // Engine cowling on the roof, behind the mast, and its intake lip.
    placed(new CapsuleGeometry(0.42, 1.5, 4, 14), 0, BODY_Y + 1.02, 0.45, Math.PI / 2).scale(0.85, 1, 1),
    // The boom, tapering aft: a cylinder's top is its front once turned.
    placed(new CylinderGeometry(0.3, 0.14, 1.9, 14), 0, BOOM_Y, 2.7, -Math.PI / 2),
    placed(new CylinderGeometry(0.14, 0.1, 2.6, 12), 0, BOOM_Y, 4.9, -Math.PI / 2),
    // Fin, swept back, with its ventral skeg under the boom.
    fin([[4.95, BOOM_Y - 0.1], [5.55, BOOM_Y - 0.05], [6.05, BOOM_Y + 1.35], [5.7, BOOM_Y + 1.4], [5.05, BOOM_Y + 0.2]], 0.1),
    fin([[5.25, BOOM_Y - 0.05], [5.7, BOOM_Y - 0.6], [5.9, BOOM_Y - 0.55], [5.65, BOOM_Y]], 0.08),
    // Stabiliser, with endplates.
    placed(new BoxGeometry(2.0, 0.06, 0.5), 0, BOOM_Y + 0.02, 4.4),
    placed(new BoxGeometry(0.06, 0.5, 0.45), -1.0, BOOM_Y + 0.12, 4.42),
    placed(new BoxGeometry(0.06, 0.5, 0.45), 1.0, BOOM_Y + 0.12, 4.42),
  ]);
  const glazing = merge([
    hull(-2.96, NOSE_Z, 0, ...span(NOSE_GLASS)),
    hull(NOSE_Z, CABIN_Z, 0, ...span(RIGHT_WINDOW)),
  ]);
  const livery = merge([
    // A band along each flank under the windows; the left one's front is on the door.
    hull(NOSE_Z, 1.7, 0.014, ...span(STRIPE_RIGHT)),
    hull(CABIN_Z, 1.7, 0.014, ...span(STRIPE_LEFT)),
  ]);
  const trim = merge([
    // Red bands round the boom.
    placed(new CylinderGeometry(0.27, 0.27, 0.3, 14, 1, true), 0, BOOM_Y, 2.2, Math.PI / 2),
    placed(new CylinderGeometry(0.122, 0.122, 0.25, 12, 1, true), 0, BOOM_Y, 5.5, Math.PI / 2),
  ]);
  // The cabin's inside: its lining — all but the doorway — and a bulkhead behind the seats.
  const bulkhead = new CircleGeometry(radiusAt(CABIN_Z) - 0.03, 24);
  bulkhead.rotateY(Math.PI);
  bulkhead.scale(1, 1.1, 1);
  bulkhead.translate(0, BODY_Y, CABIN_Z);
  const cabinLining = merge([
    hull(-2.9, NOSE_Z, -0.035),
    hull(NOSE_Z, CABIN_Z, -0.035, DOOR_SILL, TAU - (DOOR_SILL - DOOR_WINDOW[0])),
  ]);
  const floorY = HELI_SEAT.hipY - 0.42;
  const cabin: BufferGeometry[] = [
    placed(new BoxGeometry(1.25, 0.06, 1.9), 0, floorY, -0.85),
    // Instrument panel, sloped toward the pilots, and its glare shield.
    placed(new BoxGeometry(1.2, 0.34, 0.1), 0, floorY + 0.72, -1.72, -0.5),
    placed(new BoxGeometry(1.24, 0.04, 0.3), 0, floorY + 0.9, -1.78),
    bulkhead,
  ];
  const seats: BufferGeometry[] = [];
  for (const x of [HELI_SEAT.x, -HELI_SEAT.x]) {
    seats.push(placed(new BoxGeometry(0.5, 0.1, 0.5), x, HELI_SEAT.hipY - 0.1, HELI_SEAT.hipZ + 0.05));
    seats.push(placed(new BoxGeometry(0.5, 0.78, 0.1), x, HELI_SEAT.hipY + 0.3, HELI_SEAT.hipZ + 0.33, 0.12));
    // Cyclic between the knees, and the pedals.
    cabin.push(placed(new CylinderGeometry(0.018, 0.022, 0.62, 6), x, floorY + 0.31, HELI_SEAT.wheelZ, 0.2));
    for (const side of [-0.13, 0.13]) cabin.push(placed(new BoxGeometry(0.09, 0.03, 0.2), x + side, HELI_SEAT.pedalY - 0.05, HELI_SEAT.pedalZ, 0.6));
  }

  const hardware = merge([
    // Mast and the exhaust stub out of the cowling.
    placed(new CylinderGeometry(0.1, 0.16, 0.55, 10), 0, ROTOR_Y - 0.3, MAST_Z),
    placed(new CylinderGeometry(0.16, 0.13, 0.5, 10), 0, BODY_Y + 1.1, 1.3, Math.PI / 2 + 0.35),
    // Searchlight housing under the chin.
    placed(new CylinderGeometry(0.16, 0.19, 0.22, 12), 0, BODY_Y - 0.92, -2.1),
    ...cabin,
  ]);
  const skids: BufferGeometry[] = [];
  for (const side of [-1, 1]) {
    const x = side * 1.1;
    skids.push(placed(new CylinderGeometry(0.06, 0.06, 3.3, 8), x, 0.08, -0.3, Math.PI / 2));
    // Turned up at the front, as skids are.
    skids.push(placed(new CylinderGeometry(0.06, 0.06, 0.55, 8), x, 0.27, -2.15, -(Math.PI / 2 - 0.75)));
  }
  // The boarding step under the door, on a bracket from the skid.
  skids.push(placed(new BoxGeometry(0.28, 0.04, 0.4), HELI_STEP.x, HELI_STEP.y, HELI_STEP.z));
  skids.push(placed(new BoxGeometry(0.04, HELI_STEP.y - 0.1, 0.04), HELI_STEP.x + 0.08, (HELI_STEP.y + 0.1) / 2, HELI_STEP.z));
  // Cross tubes, arched up to the belly: lower forward, where it is fuller.
  for (const [z, top] of [[-1.25, 0.96], [0.75, 1.1]]) {
    skids.push(tube([[-1.1, 0.08, z], [-1.02, 0.55, z], [-0.62, top + 0.06, z], [0, top, z], [0.62, top + 0.06, z], [1.02, 0.55, z], [1.1, 0.08, z]], 0.055));
  }
  const skidTubes = merge(skids);

  // Main rotor, drawn about its own hub: four tapered blades, painted tips.
  const blades: BufferGeometry[] = [];
  const tips: BufferGeometry[] = [];
  for (let i = 0; i < 4; i++) {
    const a = (i * Math.PI) / 2;
    const blade = new CylinderGeometry(0.2, 0.15, 5.1, 4, 1);
    blade.rotateZ(Math.PI / 2);
    blade.scale(1, 0.18, 1);
    blade.translate(3.0, 0, 0);
    blade.rotateY(a);
    blades.push(blade);
    const tip = new BoxGeometry(0.4, 0.05, 0.3);
    tip.translate(5.35, 0, 0);
    tip.rotateY(a);
    tips.push(tip);
    const grip = new BoxGeometry(0.5, 0.1, 0.16);
    grip.translate(0.35, 0, 0);
    grip.rotateY(a);
    blades.push(grip);
  }
  blades.push(new CylinderGeometry(0.24, 0.26, 0.18, 12));
  blades.push(placed(new SphereGeometry(0.14, 10, 6), 0, 0.12, 0));

  const tailParts: BufferGeometry[] = [placed(new CylinderGeometry(0.1, 0.1, 0.12, 10), 0, 0, 0, 0, 0, Math.PI / 2)];
  for (let i = 0; i < 2; i++) {
    const b = new BoxGeometry(0.05, 1.9, 0.18);
    b.rotateX((i * Math.PI) / 2);
    tailParts.push(b);
  }

  // A faint disc where the blades are a blur, shown while they turn.
  const disc = new CircleGeometry(5.6, 40);
  disc.rotateX(-Math.PI / 2);

  return {
    body: [[shell, paint], [livery, stripe], [trim, accent], [hardware, dark], [skidTubes, metal],
      [cabinLining, lining], [merge(seats), fabric], [glazing, glass]],
    door: doorParts(),
    rotor: [[merge(blades), dark], [merge(tips), accent]],
    tail: merge(tailParts),
    disc,
    light: new SphereGeometry(0.07, 8, 6),
    lens: new CircleGeometry(0.14, 12),
  };
}

/** The door: its window, its panel under it with the stripe, its lining; about the hinge. */
function doorParts(): Array<[BufferGeometry, Material]> {
  const parts: Array<[BufferGeometry, Material]> = [
    [hull(NOSE_Z, CABIN_Z, 0, ...span(DOOR_WINDOW)), glass],
    [hull(NOSE_Z, CABIN_Z, 0, DOOR_WINDOW[1], DOOR_SILL - DOOR_WINDOW[1]), paint],
    [hull(NOSE_Z, CABIN_Z, 0.014, ...span(STRIPE_LEFT)), stripe],
    [hull(NOSE_Z, CABIN_Z, -0.035, DOOR_WINDOW[0], DOOR_SILL - DOOR_WINDOW[0]), lining],
  ];
  for (const [g] of parts) {
    g.translate(-DOOR_HINGE.x, -DOOR_HINGE.y, -DOOR_HINGE.z);
    g.computeBoundingSphere();
  }
  return parts;
}

// --- lights --------------------------------------------------------------------

let glowTexture: CanvasTexture | undefined;

/** A soft round glow, for a lamp seen at night. */
function glow(): CanvasTexture {
  if (glowTexture !== undefined) return glowTexture;
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.45)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  glowTexture = new CanvasTexture(canvas);
  return glowTexture;
}

interface Lamp {
  material: MeshStandardMaterial;
  halo: Sprite;
  /** Brightness 0..1 at time t, while the machine is running. */
  pattern: (t: number) => number;
  /** How big its glow is, metres. */
  size: number;
}

/** A red beacon's double flash, about once a second. */
const beaconFlash = (offset: number) => (t: number) => {
  const p = (t + offset) % 1.1;
  return p < 0.08 || (p > 0.2 && p < 0.28) ? 1 : 0;
};
const strobeFlash = (t: number) => ((t + 0.4) % 1.5 < 0.06 ? 1 : 0);
const steady = () => 1;

export interface HelicopterModel {
  root: Group;
  /** The pilot's door, hinged at its front edge: `rotation.y` negative opens it. */
  door: Group;
  mainRotor: Group;
  tailRotor: Group;
  /** The rotor's blur, shown while it turns. */
  disc: Mesh;
  lamps: Lamp[];
  /** The searchlight's lens under the chin: where its beam starts. */
  lens: Object3D;
}

/**
 * Build the airframe. The group's origin sits on the ground beneath the skids,
 * so placing it is "put it where the terrain is" with no offset bookkeeping.
 */
export function createHelicopter(): HelicopterModel {
  airframe ??= buildAirframe();
  const root = new Group();
  const add = (parent: Group, geometry: BufferGeometry, material: Material) => {
    const mesh = new Mesh(geometry, material);
    mesh.castShadow = material !== glass && material !== lining;
    mesh.receiveShadow = material !== glass;
    parent.add(mesh);
  };
  for (const [geometry, material] of airframe.body) add(root, geometry, material);
  const door = new Group();
  door.position.copy(DOOR_HINGE);
  for (const [geometry, material] of airframe.door) add(door, geometry, material);
  root.add(door);

  const mainRotor = new Group();
  mainRotor.position.set(0, ROTOR_Y, MAST_Z);
  for (const [geometry, material] of airframe.rotor) {
    const mesh = new Mesh(geometry, material);
    mesh.castShadow = true;
    mainRotor.add(mesh);
  }
  root.add(mainRotor);
  const disc = new Mesh(airframe.disc, discMaterial);
  disc.position.set(0, ROTOR_Y - 0.02, MAST_Z);
  disc.visible = false;
  disc.renderOrder = 5;
  root.add(disc);

  const tailRotor = new Group();
  tailRotor.position.set(0.2, BOOM_Y + 0.75, 5.75);
  const tail = new Mesh(airframe.tail, dark);
  tail.castShadow = true;
  tailRotor.add(tail);
  root.add(tailRotor);

  // Lamps: each machine's own materials, as they are lit one by one.
  const lamps: Lamp[] = [];
  const lamp = (colour: number, x: number, y: number, z: number, pattern: Lamp['pattern'], size: number) => {
    const material = new MeshStandardMaterial({ color: new Color(colour).multiplyScalar(0.35), emissive: new Color(colour), emissiveIntensity: 0, roughness: 0.3 });
    const mesh = new Mesh(airframe!.light, material);
    mesh.position.set(x, y, z);
    root.add(mesh);
    const halo = new Sprite(new SpriteMaterial({ map: glow(), color: new Color(colour), blending: AdditiveBlending, depthWrite: false, transparent: true }));
    halo.position.set(x, y, z);
    halo.scale.setScalar(size);
    halo.visible = false;
    root.add(halo);
    lamps.push({ material, halo, pattern, size });
  };
  lamp(0xff2a1a, 0, BODY_Y + 1.5, 0.95, beaconFlash(0), 1.3);
  lamp(0xff2a1a, 0, BODY_Y - 1.12, 0.1, beaconFlash(0.55), 1.1);
  lamp(0xff2a1a, -1.04, BOOM_Y + 0.4, 4.42, steady, 0.55);
  lamp(0x2aff6a, 1.04, BOOM_Y + 0.4, 4.42, steady, 0.55);
  lamp(0xffffff, 0, BOOM_Y + 1.42, 5.88, strobeFlash, 1.8);

  const lens = new Mesh(airframe.lens, lensMaterial);
  lens.position.set(0, BODY_Y - 1.035, -2.1);
  lens.rotation.x = Math.PI / 2;
  root.add(lens);

  root.traverse(o => { if (o instanceof Sprite) o.castShadow = false; });
  return { root, door, mainRotor, tailRotor, disc, lamps, lens };
}

const discMaterial = new MeshBasicMaterial({ color: new Color(0x1b1c20), transparent: true, opacity: 0.14, depthWrite: false, side: DoubleSide });
/** The searchlight's lens: shared, lit while the light is on. */
export const lensMaterial = new MeshStandardMaterial({ color: new Color(0xdfe6ea), emissive: new Color(0xfff6e0), emissiveIntensity: 0, roughness: 0.1, metalness: 0.3 });

/**
 * Light the machine's lamps: flashing while it runs, bright and haloed at
 * night; dark when it is parked.
 */
export function animateHelicopterLights(model: HelicopterModel, running: boolean, night: number, time: number): void {
  for (const l of model.lamps) {
    const on = running ? l.pattern(time) : 0;
    l.material.emissiveIntensity = on * (1.2 + 3 * night);
    l.halo.visible = on > 0 && night > 0.05;
    if (l.halo.visible) l.halo.material.opacity = Math.min(1, night * 1.4);
  }
  model.disc.visible = running;
}

/**
 * Repaint a helicopter in another livery: its flash along the flanks and the
 * bands on its boom in colours of their own (a police machine's), on copies
 * of those materials, so the others keep theirs.
 */
export function repaintHelicopter(model: HelicopterModel, flash: number, bands: number): void {
  const flashed = stripe.clone(), banded = accent.clone();
  flashed.color.setHex(flash);
  banded.color.setHex(bands);
  model.root.traverse((o) => {
    if (!(o instanceof Mesh)) return;
    if (o.material === stripe) o.material = flashed;
    else if (o.material === accent) o.material = banded;
  });
}

/** Let go of the machine's own materials; the geometry is shared and stays. */
export function disposeHelicopter(model: HelicopterModel): void {
  for (const l of model.lamps) {
    l.material.dispose();
    l.halo.material.dispose();
  }
}

/** Point the airframe the way the physics thinks it is pointing. */
export function orientHelicopter(
  object: Object3D,
  yaw: number,
  pitch: number,
  roll: number,
): void {
  object.rotation.set(pitch, yaw, roll, 'YXZ');
}

// --- the searchlight -------------------------------------------------------------

/**
 * The searchlight: one real light, shared by whichever machine is flown,
 * and the faint shaft of it in the air. A light joining the scene recompiles
 * the materials that see it, so it joins only when it is needed — flying at
 * night — and stays until morning.
 */
export class Searchlight {
  readonly light = new SpotLight(0xfff4e2, 0, 480, 0.2, 0.55, 1.2);
  readonly shaft: Mesh;
  private inScene = false;
  private readonly from = new Vector3();
  private readonly to = new Vector3();

  constructor(private readonly scene: Scene) {
    const length = 90;
    const radius = Math.tan(0.2) * length;
    const cone = new ConeGeometry(radius, length, 28, 1, true);
    cone.translate(0, -length / 2, 0);
    cone.rotateX(-Math.PI / 2);
    this.shaft = new Mesh(cone, new MeshBasicMaterial({
      color: new Color(0xfff2d8), alphaMap: shaftFade(), transparent: true, opacity: 0,
      blending: AdditiveBlending, depthWrite: false, side: DoubleSide,
    }));
    this.shaft.frustumCulled = false;
    this.shaft.renderOrder = 6;
  }

  /**
   * Shine from `model`'s lens along `direction` (scene axes, unit), or turn
   * off with no model. `night` 0..1 sets how strong it is.
   */
  update(model: HelicopterModel | undefined, direction: Vector3, night: number): void {
    const on = model !== undefined && night > 0.08;
    if (on && !this.inScene) {
      this.scene.add(this.light, this.light.target, this.shaft);
      this.inScene = true;
    } else if (!on && this.inScene && night <= 0.02) {
      this.scene.remove(this.light, this.light.target, this.shaft);
      this.inScene = false;
    }
    lensMaterial.emissiveIntensity = on ? 4 * night : 0;
    if (!this.inScene) return;
    this.light.intensity = on ? 1400 * night : 0;
    (this.shaft.material as MeshBasicMaterial).opacity = on ? 0.16 * night : 0;
    this.shaft.visible = on;
    if (!on) return;
    model.lens.getWorldPosition(this.from);
    this.to.copy(direction).multiplyScalar(100).add(this.from);
    this.light.position.copy(this.from);
    this.light.target.position.copy(this.to);
    this.light.target.updateMatrixWorld();
    this.shaft.position.copy(this.from);
    this.shaft.lookAt(this.to);
  }
}

/** Bright at the lamp, fading out along the shaft. */
function shaftFade(): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 4;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 0, 128);
  g.addColorStop(0, 'rgb(255,255,255)');
  g.addColorStop(0.35, 'rgb(90,90,90)');
  g.addColorStop(1, 'rgb(0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 4, 128);
  return new CanvasTexture(canvas);
}
